import { Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { PublicationSettingSearchableContentType } from 'data/generated/graphql';
import styles from './ModuleItemCount.module.scss';

const getContentTypeForDisplay = (contentTypes?: PublicationSettingSearchableContentType[]) => {
  if (contentTypes?.length === 1) {
    switch (contentTypes[0]) {
      case PublicationSettingSearchableContentType.CommerceProduct:
        return 'products';
      case PublicationSettingSearchableContentType.OxanAnnouncements:
        return 'announcements';
      default:
        return `${contentTypes[0].toLowerCase()}s`;
    }
  }
  return 'items';
};

interface ModuleItemCountProps {
  count: number;
  min?: number;
  max?: number;
  showMaxInDenominator?: boolean;
  contentTypes?: PublicationSettingSearchableContentType[];
  considerZeroMin?: boolean;
  inMultiZoneModule?: boolean;
}
export const ModuleItemCount = ({
  count,
  min,
  max,
  showMaxInDenominator,
  contentTypes,
  considerZeroMin,
  inMultiZoneModule
}: ModuleItemCountProps) => {
  let denominator;
  if (showMaxInDenominator) {
    denominator = max;
  } else if (min && !max) {
    denominator = count < min ? min : null;
  } else if (max && !min) {
    denominator = count > max ? max : null;
  } else if (max && min) {
    denominator = count < min ? min : count > max ? max : null;
  }

  const emptyDraggable = count === 0 ? <span>&nbsp;</span> : null;

  if (!denominator) {
    return emptyDraggable;
  }

  const contentType = getContentTypeForDisplay(contentTypes);

  let total;
  if (min === denominator) {
    total = denominator;
  } else if (!min) {
    if (considerZeroMin) {
      total = `${min}-${denominator}`;
    } else {
      total = denominator;
    }
  } else {
    total = `${min}-${denominator}`;
  }

  if (total === count || count >= (max ?? count)) {
    return emptyDraggable;
  }

  let label = `${count}/${total} ${contentType}`;

  if (!max || max >= 99) {
    label = `${count}/${min} or more ${contentType}`;

    if (min === 0) {
      return emptyDraggable;
    }
  }

  return (
    <div
      className={classnames(styles.numItemsMessage, { [styles.inMultizoneModule]: !!inMultiZoneModule })}
      data-testid="page-item-count"
    >
      <Wrapper padding={{ all: 'sm' }} data-testid="page-item-count-msg">
        <Typography variant="note">{label}</Typography>
      </Wrapper>
    </div>
  );
};
